import { lazy, Suspense, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useParams } from 'react-router-dom';



import { Loader, useDebouncedCallback } from '@ds';
import { LayoutTheme, QuestId, useAppActions, useMainQuest, useMainQuestActions, useMainQuestLoadingState, useOverlayActions, useOverlayTrigger, useQuestViewModeActions, useUserBankTaps } from '@store';



import { LayoutWrapper } from '../layout/LayoutWrapper';
import { useQuestBlocked } from '../hooks/useQuestBlocked';


type RenderableQuestId = Exclude<
  QuestId,
  QuestId.Hottei | QuestId.Split | QuestId.Not | QuestId.Rhomb | QuestId.Spiral
>

const questComponents: Record<
  RenderableQuestId,
  React.LazyExoticComponent<React.ComponentType<any>>
> = {
  camera_cabinet: lazy(() =>
    import('../camera').then((mod) => ({ default: mod.Camera }))
  ),
  nightsong: lazy(() =>
    import('../nightsong').then((mod) => ({ default: mod.Nightsong }))
  ),
  columns: lazy(() =>
    import('../columns').then((mod) => ({ default: mod.Columns }))
  ),
  flowers: lazy(() =>
    import('../flowers').then((mod) => ({ default: mod.Flowers }))
  ),
  eye: lazy(() => import('../eye').then((mod) => ({ default: mod.Eye }))),
  cipher: lazy(() =>
    import('../cipher-wrapper').then((mod) => ({ default: mod.CipherWrapper }))
  ),
  knot_harlan: lazy(() =>
    import('../knot').then((mod) => ({ default: mod.KnotWrapper }))
  ),
  altar_eye: lazy(() =>
    import('../eyegate').then((mod) => ({ default: mod.EyeGate }))
  ),
  forest: lazy(() =>
    import('../forest').then((mod) => ({ default: mod.Forest }))
  ),
  camera: lazy(() =>
    import('../camera').then((mod) => ({ default: mod.Camera }))
  ),
  string: lazy(() =>
    import('../string').then((mod) => ({ default: mod.String }))
  ),

  TapTap: lazy(() =>
    import('../TapTap').then((mod) => ({ default: mod.TapTap }))
  ),
  pda: lazy(() => import('../KPK').then((mod) => ({ default: mod.KPK }))),
  constellation: lazy(() =>
    import('../constellation-wrapper').then((mod) => ({
      default: mod.ConstellationWrapper,
    }))
  ),
  access: lazy(() =>
    import('../input-quest-wrapper').then((mod) => ({ default: mod.InputQuestWrapper }))
  ),
  knot_ksarat: lazy(() =>
    import('../knot').then((mod) => ({ default: mod.KnotWrapper }))
  ),
  knot_asterion: lazy(() =>
    import('../knot').then((mod) => ({ default: mod.KnotWrapper }))
  ),
  books: lazy(() => import('../book').then((mod) => ({ default: mod.Books }))),
  hotey: lazy(() => import('../hotey').then((mod) => ({ default: mod.Hotey }))),
  question_mark: lazy(() =>
    import('../cipher-wrapper').then((mod) => ({ default: mod.CipherWrapper }))
  ),
  lock: lazy(() => import('../lock').then((mod) => ({ default: mod.Lock }))),
  riddle: lazy(() => import('../cipher-wrapper').then((mod) => ({ default: mod.CipherWrapper }))),
  numbers: lazy(() => import('../numbers').then((mod) => ({ default: mod.Numbers }))),
  dots: lazy(() => import('../constellation-wrapper').then((mod) => ({ default: mod.ConstellationWrapper }))),
  camera_panelka: lazy(() =>
    import('../camera').then((mod) => ({ default: mod.Camera }))),
  knot_vira: lazy(() => import('../knot').then((mod) => ({ default: mod.KnotWrapper }))),
  panelka: lazy(() => import('../panelka').then((mod) => ({ default: mod.Panelka }))),
  next: lazy(() => import('../cipher-wrapper').then((mod) => ({ default: mod.CipherWrapper }))),
  message: lazy(() => import('../input-quest-wrapper').then((mod) => ({ default: mod.InputQuestWrapper }))),
  elements: lazy(() => import('../input-quest-wrapper').then((mod) => ({ default: mod.InputQuestWrapper }))),
  knot_ruviy: lazy(() => import('../knot').then((mod) => ({ default: mod.KnotWrapper }))),
  expansion: lazy(() => import('../cipher-wrapper').then((mod) => ({ default: mod.CipherWrapper }))),
  counting: lazy(() => import('../input-quest-wrapper').then((mod) => ({ default: mod.InputQuestWrapper }))),
  fragmentation: lazy(() => import('../input-quest-wrapper').then((mod) => ({ default: mod.InputQuestWrapper }))),
  switch: lazy(() => import('../switch').then((mod) => ({ default: mod.SwitchQuest }))),
  oscilloscope_1: lazy(() => import('../oscilloscope').then((mod) => ({ default: mod.OscilloscopeWrapper }))),
  camera_oscilloscope: lazy(() =>
    import('../camera').then((mod) => ({ default: mod.Camera }))),
  changes: lazy(() => import('../constellation-wrapper').then((mod) => ({ default: mod.ConstellationWrapper }))),
  knot_morian: lazy(() => import('../knot').then((mod) => ({ default: mod.KnotWrapper }))),
  spheres: lazy(() => import('../disk').then((mod) => ({ default: mod.Disk }))),
  oscilloscope_2: lazy(() => import('../oscilloscope').then((mod) => ({ default: mod.OscilloscopeWrapper }))),
  duality: lazy(() => import('../duality-gate').then((mod) => ({ default: mod.DualityGate }))),
  knot_ailin: lazy(() => import('../knot').then((mod) => ({ default: mod.KnotWrapper }))),
  oscilloscope_3: lazy(() => import('../oscilloscope').then((mod) => ({ default: mod.OscilloscopeWrapper }))),
  volume: lazy(() => import('../switch').then((mod) => ({ default: mod.SwitchQuest }))),
  quietude: lazy(() => import('../toggler').then(mod => ({ default: mod.TogglerWrapper }))),
  not_quest: lazy(() => import('../not-quest').then((mod) => ({ default: mod.NotQuest }))),
  mirror: lazy(() => import('../mirror').then((mod) => ({ default: mod.Mirror }))),
  shadow: lazy(() => import('../cipher-wrapper').then((mod) => ({ default: mod.CipherWrapper }))),
  chain: lazy(() => import('../input-quest-wrapper').then((mod) => ({ default: mod.InputQuestWrapper }))),
  camera_mirror: lazy(() => import('../camera').then((mod) => ({ default: mod.Camera }))),
  box: lazy(() => import('../box').then((mod) => ({ default: mod.BoxWrapper }))),
  void: lazy(() => import('../cipher-wrapper').then((mod) => ({ default: mod.CipherWrapper }))),
  decrypt: lazy(() => import('../input-quest-wrapper').then((mod) => ({ default: mod.InputQuestWrapper }))),
  knot_safaris: lazy(() => import('../knot').then((mod) => ({ default: mod.KnotWrapper }))),
  knot_fragments: lazy(() => import('../knot').then((mod) => ({ default: mod.KnotWrapper }))),
  curve: lazy(() => import('../constellation-wrapper').then((mod) => ({ default: mod.ConstellationWrapper }))),
  lights: lazy(() => import('../toggler').then((mod) => ({ default: mod.TogglerWrapper }))),
  gap: lazy(() => import('../gap').then((mod) => ({ default: mod.Gap }))),
  moment: lazy(() => import('../moment').then((mod) => ({ default: mod.MomentQuest }))),
  cassete: lazy(() => import('../cassete').then((mod) => ({ default: mod.CasseteQuest }))),
  grid: lazy(() => import('../input-quest-wrapper').then((mod) => ({ default: mod.InputQuestWrapper }))),
  order: lazy(() => import('../order').then((mod) => ({ default: mod.Order }))),
  playback: lazy(() => import('../playback').then((mod) => ({ default: mod.Playback }))),
  assembly: lazy(() => import('../input-quest-wrapper').then((mod) => ({ default: mod.InputQuestWrapper }))),
  whisper: lazy(() => import('../cipher-wrapper').then((mod) => ({ default: mod.CipherWrapper }))),
  hollow: lazy(() => import('../input-quest-wrapper').then((mod) => ({ default: mod.InputQuestWrapper }))),
  resolve: lazy(() => import('../resolve').then((mod) => ({ default: mod.Resolve }))),
  ring: lazy(() => import('../ring').then((mod) => ({ default: mod.RingQuest }))),
  seal: lazy(() => import('../input-quest-wrapper').then((mod) => ({ default: mod.InputQuestWrapper }))),
  error: lazy(() => import('../error').then((mod) => ({ default: mod.ErrorQuest }))),
  call: lazy(() => import('../call').then((mod) => ({ default: mod.CallQuest }))),
}

export const QuestRenderer = () => {
  const { questId } = useParams()
  const { t } = useTranslation()
  const isMainError = useMainQuest.store.isError()
  const {
    init: initQuest,
    reset: resetQuest,
    setIsLoading: setIsLoadingMainQuest,
    setQuestDialog,
  } = useMainQuestActions()
  const { questBlocked } = useQuestBlocked()
  const isMainQuestLoading = useMainQuestLoadingState()
  const { setIsLoading } = useAppActions()
  const { triggerOverlay } = useOverlayTrigger()
  const { setOverlayQueue } = useOverlayActions();
  const { setMode: setQuestViewMode } = useQuestViewModeActions()
  const debouncedSetQuestViewMode = useDebouncedCallback(setQuestViewMode, 500)

  const questDialog = useMainQuest.store.questDialog()

  const availableStars = useMainQuest.store.availableStars()
  const taps = useUserBankTaps()

  const isEndTriggered = useRef<boolean>(false)
  const isRefillTriggered = useRef<boolean>(false)

  useEffect(() => {
    localStorage.setItem('last-quest-slug', JSON.stringify({
      slug: questId,
      createdAt: Date.now()
    }))
  }, [])
  const onEndQuest = useCallback(async () => {
    if (isEndTriggered.current) return
    isEndTriggered.current = true
    const hasQuestDialog = await setQuestDialog(questId as QuestId)
    if (questId && hasQuestDialog) {
      triggerOverlay(
        questId as string,
        [
          {
            type: 'dialog',
            payload: {
              slug: questId as string,
            },
          },
        ],
        true
      )
    }
    triggerOverlay('questCompleted', undefined, true)
  }, [questId, isEndTriggered.current, questDialog, triggerOverlay])

  useEffect(() => {
    if (isRefillTriggered.current) return

    const prevShownDate = localStorage.getItem('refill-popup-shown-date') || "";
    if (prevShownDate &&
      Date.now() - Number(prevShownDate) < 6 * 1000 * 60 * 60
    ) return
    if (availableStars.length && taps && !taps.balance) {
      isRefillTriggered.current = true;
      setOverlayQueue([{ type: 'refill' }])
      localStorage.setItem('refill-popup-shown-date', Date.now().toString())
    }
  }, [availableStars, taps, isRefillTriggered])

  useEffect(() => {
    setIsLoading(isMainQuestLoading)
  }, [isMainQuestLoading])

  useEffect(() => {
    if (questId === 'TapTap') {
      setIsLoadingMainQuest(false)
    } else if (questId) {
      initQuest(questId as QuestId);
      localStorage.setItem('last-quest-slug', JSON.stringify({
        slug: questId,
        createdAt: Date.now()
      }))
    }

    return () => {
      resetQuest()
    }
  }, [questId])

  useEffect(() => {
    if (availableStars.length) {
      debouncedSetQuestViewMode('stars')
    } else {
      debouncedSetQuestViewMode('quest')
    }
  }, [availableStars?.length])

  const QuestComponent =
    questComponents[questId as keyof typeof questComponents]

  if (isMainQuestLoading) {
    return <Loader />
  }
  if (questBlocked) {
    return <Navigate to="/home" replace />
  }
  if (!QuestComponent || !questId) {
    return <Navigate to="/home" replace />
  }

  if (isMainError && questId !== 'TapTap') {
    // TODO: Нужен экран с ошибкой
    return <div>{t('questRenderer.error')}</div>
  }

  return (
    <LayoutWrapper layoutTheme={LayoutTheme.QUEST}>
      <Suspense fallback={<Loader />}>
        <QuestComponent key={questId} onEndQuest={onEndQuest} />
      </Suspense>
    </LayoutWrapper>
  )
}
